/**
 * CinematicHero — role-journey cinematic tour.
 *
 * The public landing page's job is to move visitors into the part of the
 * ecosystem they want to join. This component scrolls through the FIVE
 * roles in MedexOVA's Verified Transportation Infrastructure — Rider, Driver,
 * Broker, Dispatcher, and Facility — with a 3D-perspective mockup, a "why
 * this is the new bar" one-liner, and a 3-click CTA (Apply/Book + QR + call).
 *
 * ⚠️  Constitution / Contract §Public-PHI-Firewall:
 *      Every value visible on this component is FICTIONAL. No real broker,
 *      dispatcher, facility, driver, or rider name appears. No real ride
 *      IDs, prices, or PHI. If you edit this file, keep it that way.
 *
 * All fake names below are invented for marketing. They are NOT partners,
 * NOT customers, and NOT drawn from any real database record.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  DeviceMobile,
  UsersThree,
  CheckCircle,
  Hospital,
  User,
  Broadcast,
  Buildings,
  Headset,
  QrCode,
  Phone,
} from "@phosphor-icons/react";

// ─────────────────────────────────────────────────────────────
// FICTIONAL DATA · marketing surface only, never from DB
// ─────────────────────────────────────────────────────────────

const FAKE_BROKER = "Meridian Health Network";
const FAKE_BROKER_SHORT = "Meridian";
const FAKE_TRANSPORT_A = "Northstar Care Partners";
const FAKE_TRANSPORT_B = "Blue Horizon Transport";
const FAKE_TRANSPORT_C = "Cedar Ridge Mobility";
const FAKE_TRANSPORT_D = "Silverline Rideshare";
const FAKE_DRIVER = "Sam Vega";
const FAKE_DISPATCHER = "J. Rivera";
const FAKE_FACILITY = "Community Hospital North";
const FAKE_RIDER_INIT = "R. Patel";
const FAKE_TRIP_ID = "A-7742";
const FAKE_HASH = "8f2c34…6f9e";
const FAKE_ONBOARD_CODE = "MEDEXOVA-ONBOARD";

// Support / onboarding hotline — real, working AI onboarding line (see
// backend/onboarding_program.py). Not the exact ONBOARD vanity (unavailable
// for purchase) but close: 833-435-6626.
const HOTLINE_DISPLAY = "1-833-435-6626";
const HOTLINE_TEL = "tel:+18334356626";

// ─────────────────────────────────────────────────────────────
// Journey definitions
// ─────────────────────────────────────────────────────────────

const JOURNEYS = [
  {
    id: "rider",
    persona: "For riders & families",
    kicker: "Journey 01 · Book",
    title: "Book in 3 taps. Get a verified receipt — not a promise.",
    pitch:
      "Every trip stitches identity, credentials, rule execution, and chain-of-custody into one packet you can hand a payer or a caseworker. Peace of mind is the product.",
    bar: "Chain-of-custody · every stop timestamped and signed.",
    accent: "#3B82F6",
    ring: "rgba(59,130,246,0.32)",
    Icon: User,
    ctaPrimary: { to: "/book", label: "Book a verified trip" },
    ctaSecondary: { to: "/ride/new", label: "Create an account" },
    Mockup: RiderMockup,
  },
  {
    id: "driver",
    persona: "For drivers",
    kicker: "Journey 02 · Drive",
    title: "No app to download. The Beacon finds you when you're ready to work.",
    pitch:
      "Apply from your phone in under 3 minutes. Enroll a single selfie once and every future pickup verifies in a second. Open the Beacon link — jobs light up when you're on-duty.",
    bar: "Portable identity · one enrollment, every future trip verified.",
    accent: "#F59E0B",
    ring: "rgba(245,158,11,0.32)",
    Icon: DeviceMobile,
    ctaPrimary: { to: "/drive/new", label: "Apply to drive" },
    ctaSecondary: { qr: true, label: "Scan to onboard on phone" },
    Mockup: DriverMockup,
  },
  {
    id: "broker",
    persona: "For brokers & payers",
    kicker: "Journey 03 · Ingest",
    title: "Offers ingested in 15 seconds. POD-back the moment the ride ends.",
    pitch:
      "Fast-lane parser + portal-side scanner both feed one queue. Your ops team gets a receipt cryptographically stitched from identity + credential + rule + chain-of-custody — audit-ready, payer-ready, no spreadsheets.",
    bar: "Trust cycle · every offer closes with a signed compliance packet.",
    accent: "#10B981",
    ring: "rgba(16,185,129,0.32)",
    Icon: Broadcast,
    ctaPrimary: { to: "/partners?role=broker", label: "Partner with MedexOVA" },
    ctaSecondary: { to: "/partners/apply", label: "Apply as a fleet provider" },
    Mockup: BrokerMockup,
  },
  {
    id: "dispatcher",
    persona: "For dispatchers & VAs",
    kicker: "Journey 04 · Confirm",
    title: "One-tap approval. Sensory alerts. Never miss a trip.",
    pitch:
      "Auto-selector short-lists the top 4 candidates the moment an offer lands. A visual + haptic + sound alert lands on every screen you own. You tap — the trip is placed, notifications fire, chain-of-custody starts.",
    bar: "Human-in-the-loop · seconds, not minutes, from offer to placed.",
    accent: "#B45309",
    ring: "rgba(180,83,9,0.36)",
    Icon: Headset,
    ctaPrimary: { to: "/partners?role=dispatcher", label: "Join operations" },
    ctaSecondary: { to: "/partners?role=dispatcher", label: "How the queue works" },
    Mockup: DispatcherMockup,
  },
  {
    id: "facility",
    persona: "For facilities & health systems",
    kicker: "Journey 05 · Audit",
    title: "Every trip a compliance packet. Audit-ready by default.",
    pitch:
      "Send us the ride. We send back a signed manifest — identity verified, credentials current, every ceremony fired, POD captured and stitched. Billing, audit, and legal all get the same source of truth.",
    bar: "Verified by default · trust is the product, not a rebate.",
    accent: "#047857",
    ring: "rgba(4,120,87,0.36)",
    Icon: Buildings,
    ctaPrimary: { to: "/partners?role=facility", label: "Onboard your facility" },
    ctaSecondary: { to: "/partners?role=facility", label: "See a sample packet" },
    Mockup: FacilityMockup,
  },
];

const AUTOPLAY_MS = 6000;

// ─────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────

function useIsNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    function onResize() {
      setNarrow(window.innerWidth < breakpoint);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return narrow;
}

export default function CinematicHero() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const narrow = useIsNarrow();

  const step = JOURNEYS[i];
  const MockupComp = step.Mockup;

  const goto = useCallback((idx) => setI(((idx % JOURNEYS.length) + JOURNEYS.length) % JOURNEYS.length), []);
  const next = useCallback(() => goto(i + 1), [i, goto]);
  const prev = useCallback(() => goto(i - 1), [i, goto]);

  useEffect(() => {
    if (paused || showQr) return;
    const t = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [i, paused, showQr, next]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  return (
    <section
      data-testid="landing-cinematic-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: "relative",
        background: "linear-gradient(135deg, #0B1220 0%, #142433 50%, #0B1220 100%)",
        color: "#fff",
        overflow: "hidden",
        padding: "72px 24px 96px",
      }}
    >
      <BackdropGlow accent={step.ring} />
      <BackdropGrain />

      <div style={{ ...outerGrid, gridTemplateColumns: narrow ? "1fr" : "minmax(0, 1fr) minmax(0, 1.1fr)" }}>
        {/* LEFT — narrative */}
        <div>
          <PersonaChip step={step} />

          <AnimatePresence mode="wait">
            <motion.h1
              key={`title-${step.id}`}
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                fontSize: "clamp(34px, 4.6vw, 60px)",
                lineHeight: 1.04,
                fontWeight: 900,
                letterSpacing: "-0.03em",
                marginBottom: 20,
              }}
            >
              {step.title}
            </motion.h1>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p
              key={`pitch-${step.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.82 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55 }}
              style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 560, color: "#CBD5E1" }}
              data-testid="landing-cinematic-pitch"
            >
              {step.pitch}
            </motion.p>
          </AnimatePresence>

          {/* Trust cycle diagram — rebuilds per journey */}
          <TrustCycle accent={step.accent} activeId={step.id} />

          {/* "Why this is the new bar" */}
          <div
            style={{
              marginTop: 22,
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 14px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
            data-testid="landing-cinematic-bar"
          >
            <ShieldCheck size={16} color={step.accent} weight="fill" />
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", color: "#E2E8F0" }}>
              The new bar — {step.bar}
            </span>
          </div>

          {/* CTA row · 3-click rule */}
          <div style={{ marginTop: 30, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link
              to={step.ctaPrimary.to}
              data-testid={`landing-cinematic-cta-${step.id}-primary`}
              style={ctaPrimary(step.accent)}
            >
              {step.ctaPrimary.label} <ArrowRight size={16} weight="bold" />
            </Link>

            {step.ctaSecondary.qr ? (
              <button
                type="button"
                onClick={() => setShowQr(true)}
                data-testid={`landing-cinematic-cta-${step.id}-secondary`}
                style={ctaSecondary}
              >
                <QrCode size={16} weight="bold" />
                {step.ctaSecondary.label}
              </button>
            ) : (
              <Link
                to={step.ctaSecondary.to}
                data-testid={`landing-cinematic-cta-${step.id}-secondary`}
                style={ctaSecondary}
              >
                {step.ctaSecondary.label}
              </Link>
            )}

            <a
              href={HOTLINE_TEL}
              data-testid={`landing-cinematic-cta-${step.id}-call`}
              style={{ ...ctaSecondary, gap: 8 }}
            >
              <Phone size={16} weight="bold" />
              Call {HOTLINE_DISPLAY}
            </a>
          </div>

          {/* Stepper chips */}
          <div style={stepperRow} data-testid="landing-cinematic-stepper">
            {JOURNEYS.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                onClick={() => goto(idx)}
                data-testid={`landing-cinematic-step-${s.id}`}
                style={stepChip(idx === i, s.accent)}
                aria-label={`Go to journey ${idx + 1}: ${s.persona}`}
              >
                <s.Icon size={12} weight="fill" />
                {s.persona}
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — 3D stage */}
        <div data-testid="landing-cinematic-stage" style={{ ...stageStyle, height: narrow ? 420 : 520 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={`stage-${step.id}`}
              initial={{ opacity: 0, rotateY: -22, rotateX: 8, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, rotateY: -10, rotateX: 4, scale: 1, y: 0 }}
              exit={{ opacity: 0, rotateY: 22, rotateX: -6, scale: 0.94, y: -40 }}
              transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
              style={stageInnerStyle}
            >
              <MockupComp accent={step.accent} />
            </motion.div>
          </AnimatePresence>

          <FloatingStatus i={i} total={JOURNEYS.length} paused={paused || showQr} />
        </div>
      </div>

      {/* Progress bar */}
      <motion.div
        key={`${i}-${paused ? "p" : "r"}-${showQr ? "q" : "o"}`}
        initial={{ width: "0%" }}
        animate={{ width: paused || showQr ? "0%" : "100%" }}
        transition={{ duration: paused || showQr ? 0 : AUTOPLAY_MS / 1000, ease: "linear" }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 3,
          background: step.accent,
          pointerEvents: "none",
        }}
      />

      {showQr && <OnboardingQrModal onClose={() => setShowQr(false)} />}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared UI bits
// ─────────────────────────────────────────────────────────────

function BackdropGlow({ accent }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(1100px 520px at 70% 25%, ${accent} 0%, transparent 60%)`,
        transition: "background 1.2s ease",
        pointerEvents: "none",
      }}
    />
  );
}

function BackdropGrain() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.06'/></svg>\")",
        mixBlendMode: "overlay",
        pointerEvents: "none",
      }}
    />
  );
}

function PersonaChip({ step }) {
  return (
    <motion.div
      key={`kicker-${step.id}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 14,
        padding: "6px 12px",
        borderRadius: 999,
        background: `${step.accent}1A`,
        border: `1px solid ${step.accent}55`,
      }}
    >
      <step.Icon size={13} color={step.accent} weight="fill" />
      <span
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.22em",
          color: step.accent,
          fontWeight: 800,
        }}
      >
        {step.kicker}
      </span>
      <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>· {step.persona}</span>
    </motion.div>
  );
}

function FloatingStatus({ i, total, paused }) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        zIndex: 5,
        padding: "6px 12px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "#CBD5E1",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        backdropFilter: "blur(12px)",
      }}
    >
      {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {paused ? "paused" : "live"}
    </div>
  );
}

const TRUST_STEPS = [
  { id: "discover", label: "Discover" },
  { id: "match", label: "Match" },
  { id: "verify", label: "Verify" },
  { id: "deliver", label: "Deliver" },
  { id: "receipt", label: "Receipt" },
];

const JOURNEY_TO_TRUST_STEP = {
  rider: "receipt",
  driver: "verify",
  broker: "discover",
  dispatcher: "match",
  facility: "receipt",
};

function TrustCycle({ accent, activeId }) {
  const activeStep = JOURNEY_TO_TRUST_STEP[activeId] || "verify";
  return (
    <div
      data-testid="landing-cinematic-trust-cycle"
      style={{
        marginTop: 28,
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      {TRUST_STEPS.map((s, idx) => {
        const isActive = s.id === activeStep;
        return (
          <React.Fragment key={s.id}>
            <motion.div
              animate={{ scale: isActive ? 1.06 : 1 }}
              transition={{ duration: 0.4 }}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                background: isActive ? accent : "rgba(255,255,255,0.05)",
                color: isActive ? "#0B1220" : "#94A3B8",
                border: `1px solid ${isActive ? accent : "rgba(255,255,255,0.10)"}`,
              }}
            >
              {s.label}
            </motion.div>
            {idx < TRUST_STEPS.length - 1 && (
              <div
                aria-hidden
                style={{
                  width: 14,
                  height: 2,
                  background: "rgba(255,255,255,0.16)",
                  borderRadius: 2,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OnboardingQrModal({ onClose }) {
  const url = `${window.location.origin}/drive/new?src=hero-qr`;
  const encoded = encodeURIComponent(url);
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="landing-cinematic-qr-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,8,14,0.72)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          color: "#0B1220",
          padding: 28,
          borderRadius: 16,
          width: "min(360px, 92vw)",
          textAlign: "center",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "#B45309",
            fontWeight: 800,
            marginBottom: 6,
          }}
        >
          {FAKE_ONBOARD_CODE}
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Onboard on your phone</div>
        <p style={{ fontSize: 13, color: "#475569", marginBottom: 16 }}>
          Point your phone camera at the code. You&apos;ll land on the driver application — no app store, no download.
        </p>
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encoded}`}
          alt="QR code to open the driver onboarding page on your phone"
          data-testid="landing-cinematic-qr-image"
          style={{ width: 220, height: 220, borderRadius: 12, display: "block", margin: "0 auto" }}
        />
        <div
          style={{
            marginTop: 16,
            fontSize: 12,
            color: "#64748B",
            fontFamily: "ui-monospace, Menlo, monospace",
            wordBreak: "break-all",
          }}
        >
          {url}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="landing-cinematic-qr-close"
          style={{
            marginTop: 18,
            padding: "10px 16px",
            borderRadius: 10,
            background: "#0B1220",
            color: "#fff",
            border: "none",
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const outerGrid = {
  maxWidth: 1320,
  margin: "0 auto",
  position: "relative",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)",
  gap: 48,
  alignItems: "center",
};

const stageStyle = {
  position: "relative",
  height: 520,
  perspective: 1400,
  perspectiveOrigin: "50% 50%",
};

const stageInnerStyle = {
  position: "absolute",
  inset: 0,
  transformStyle: "preserve-3d",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const stepperRow = {
  marginTop: 26,
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

function stepChip(active, accent) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 999,
    background: active ? accent : "rgba(255,255,255,0.06)",
    color: active ? "#0B1220" : "#CBD5E1",
    border: `1px solid ${active ? accent : "rgba(255,255,255,0.14)"}`,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "all 0.25s ease",
  };
}

function ctaPrimary(accent) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 22px",
    borderRadius: 10,
    background: "#fff",
    color: "#0B1220",
    fontSize: 14,
    fontWeight: 800,
    textDecoration: "none",
    letterSpacing: "0.02em",
    boxShadow: `0 10px 30px -12px ${accent}88`,
  };
}

const ctaSecondary = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "14px 20px",
  borderRadius: 10,
  background: "transparent",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.28)",
  fontSize: 14,
  fontWeight: 800,
  textDecoration: "none",
  letterSpacing: "0.02em",
  cursor: "pointer",
};

const cardBase = {
  width: 480,
  maxWidth: "94%",
  borderRadius: 20,
  background: "rgba(255,255,255,0.96)",
  color: "#0B1220",
  boxShadow: "0 50px 100px -30px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  padding: 26,
  transform: "translateZ(20px)",
};

// ─────────────────────────────────────────────────────────────
// Mockups — ONE per role. 100% fictional data.
// ─────────────────────────────────────────────────────────────

function KickerRow({ accent, children, dot = true }) {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        color: accent,
        fontWeight: 800,
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {dot && (
        <motion.span
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          style={{ width: 8, height: 8, borderRadius: "50%", background: accent }}
        />
      )}
      {children}
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div style={{ padding: "8px 10px", borderRadius: 8, background: "#F1F5F9" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: "#0B1220", fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// -- Journey 01 · Rider — verified receipt card
function RiderMockup({ accent }) {
  const items = [
    { label: "Trip identity", val: `Trip ${FAKE_TRIP_ID}` },
    { label: "Rider", val: `${FAKE_RIDER_INIT} · verified` },
    { label: "Driver identity", val: `${FAKE_DRIVER} · face 96` },
    { label: "Pickup ceremony", val: "10:04 · signed" },
    { label: "Drop-off ceremony", val: "10:41 · POD stitched" },
  ];
  return (
    <motion.div style={cardBase} initial={{ scale: 0.98 }} animate={{ scale: 1 }} transition={{ duration: 0.6 }}>
      <KickerRow accent={accent}>Verified receipt · SHA-256 stitched</KickerRow>
      <div style={{ fontSize: 16, fontWeight: 800 }}>Trip {FAKE_TRIP_ID} — complete</div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 14, fontFamily: "ui-monospace, Menlo, monospace" }}>
        hash · {FAKE_HASH}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, idx) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.08, duration: 0.4 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 12px",
              borderRadius: 8,
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
            }}
          >
            <div style={{ fontSize: 12, color: "#1E3A8A", fontWeight: 700 }}>{it.label}</div>
            <div
              style={{
                fontSize: 12,
                color: "#1D4ED8",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <CheckCircle size={12} weight="fill" /> {it.val}
            </div>
          </motion.div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: "#475569", fontStyle: "italic" }}>
        You can hand this to a payer, a caseworker, or auditor — it stands on its own.
      </div>
    </motion.div>
  );
}

// -- Journey 02 · Driver — phone showing Beacon
function DriverMockup({ accent }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        transform: "translateZ(20px)",
      }}
    >
      {/* Phone */}
      <div
        style={{
          width: 240,
          height: 460,
          borderRadius: 34,
          background: "linear-gradient(180deg, #0F172A, #1E293B)",
          padding: 10,
          boxShadow: "0 50px 90px -30px rgba(0,0,0,0.6), 0 0 0 6px rgba(0,0,0,0.55)",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 26,
            background: "#0B1220",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* status bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 14px",
              fontSize: 10,
              color: "#94A3B8",
              fontWeight: 700,
            }}
          >
            <span>10:04</span>
            <span>Beacon · on-duty</span>
          </div>

          {/* Beacon big pulse */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 20 }}>
            <div style={{ position: "relative", width: 120, height: 120 }}>
              {[0, 0.4, 0.8].map((delay, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0.7, scale: 0.6 }}
                  animate={{ opacity: [0.7, 0], scale: [0.6, 1.6] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: accent,
                  }}
                />
              ))}
              <div
                style={{
                  position: "absolute",
                  inset: 24,
                  borderRadius: "50%",
                  background: accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#0B1220",
                }}
              >
                ON
              </div>
            </div>
            <div style={{ marginTop: 14, fontSize: 13, color: "#E2E8F0", fontWeight: 800 }}>
              {FAKE_DRIVER}
            </div>
            <div style={{ fontSize: 10, color: "#94A3B8", letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Face 96 · License current
            </div>
          </div>

          {/* Incoming offer bubble */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            style={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 20,
              background: "#fff",
              color: "#0B1220",
              padding: 12,
              borderRadius: 12,
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ fontSize: 9, letterSpacing: "0.16em", color: accent, textTransform: "uppercase", fontWeight: 800 }}>
              Offer inbound · 2.1 mi
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, marginTop: 3 }}>Trip {FAKE_TRIP_ID}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>Wheelchair · door-to-door · 10:20</div>
            <div
              style={{
                marginTop: 8,
                padding: "8px 10px",
                borderRadius: 8,
                background: accent,
                color: "#0B1220",
                textAlign: "center",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              Tap to accept
            </div>
          </motion.div>
        </div>
      </div>

      {/* Side facts card */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        style={{
          width: 220,
          borderRadius: 16,
          background: "rgba(255,255,255,0.94)",
          color: "#0B1220",
          padding: 18,
          boxShadow: "0 30px 60px -20px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: "0.2em", color: accent, fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>
          Onboarding speed
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em" }}>&lt; 3 min</div>
        <div style={{ fontSize: 11, color: "#64748B", marginBottom: 12 }}>Apply → selfie → done.</div>
        <FieldRow label="No app to download" value="Beacon lives at a URL" />
        <div style={{ height: 6 }} />
        <FieldRow label="One selfie forever" value="Every future trip verifies" />
        <div style={{ height: 6 }} />
        <FieldRow label="Portable identity" value="MedexOVA Auth token" />
      </motion.div>
    </div>
  );
}

// -- Journey 03 · Broker — ingest queue + POD-back
function BrokerMockup({ accent }) {
  const rows = [
    { time: "14:03:11", src: "Email", body: `New offer · ${FAKE_TRIP_ID} · ${FAKE_BROKER_SHORT}`, tag: "parsed" },
    { time: "14:03:14", src: "Portal", body: "Confirmed same offer via scanner", tag: "matched" },
    { time: "14:03:19", src: "VA", body: `${FAKE_DISPATCHER} tapped Assign`, tag: "placed" },
    { time: "14:41:02", src: "POD", body: `Signed · Drop-off @ ${FAKE_FACILITY}`, tag: "stitched" },
    { time: "14:41:04", src: "Broker POD-back", body: `${FAKE_BROKER_SHORT} · webhook confirmed`, tag: "closed" },
  ];
  return (
    <motion.div style={cardBase}>
      <KickerRow accent={accent}>Trust cycle · live timeline</KickerRow>
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>
        Trip {FAKE_TRIP_ID} · {FAKE_BROKER}
      </div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 14 }}>
        Offer landed → placed in 8 seconds. POD-back the moment the driver signed.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, idx) => (
          <motion.div
            key={r.time + r.src}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1, duration: 0.4 }}
            style={{
              display: "grid",
              gridTemplateColumns: "68px 60px 1fr auto",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 8,
              background: idx === rows.length - 1 ? "#ECFDF5" : "#F8FAFC",
              border: `1px solid ${idx === rows.length - 1 ? "#86EFAC" : "#E2E8F0"}`,
            }}
          >
            <span style={{ fontSize: 10, color: "#64748B", fontFamily: "ui-monospace, Menlo, monospace" }}>
              {r.time}
            </span>
            <span
              style={{
                fontSize: 9,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#0F172A",
                background: "#E2E8F0",
                padding: "2px 6px",
                borderRadius: 4,
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {r.src}
            </span>
            <span style={{ fontSize: 12, color: "#0B1220", fontWeight: 600 }}>{r.body}</span>
            <span
              style={{
                fontSize: 10,
                color: idx === rows.length - 1 ? "#047857" : accent,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {r.tag}
            </span>
          </motion.div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: "#475569" }}>
        <strong style={{ color: "#0B1220" }}>38 min</strong> total · offer to signed packet.
      </div>
    </motion.div>
  );
}

// -- Journey 04 · Dispatcher — VA one-tap queue
function DispatcherMockup({ accent }) {
  const rows = [
    { name: FAKE_TRANSPORT_A, score: 96, distance: "2.1 mi", pick: true },
    { name: FAKE_TRANSPORT_B, score: 91, distance: "3.4 mi" },
    { name: FAKE_TRANSPORT_C, score: 88, distance: "4.0 mi" },
    { name: FAKE_TRANSPORT_D, score: 84, distance: "5.2 mi" },
  ];
  return (
    <motion.div style={cardBase}>
      <KickerRow accent={accent}>VA queue · alert firing</KickerRow>
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>1 trip needs your decision</div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 12 }}>
        Haptic · sound · badge on every screen. Auto-selector already narrowed it to four.
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 12,
          background: "#FEF3C7",
          border: `1px solid ${accent}`,
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 11, color: "#92400E", fontWeight: 800, marginBottom: 4 }}>
          Trip {FAKE_TRIP_ID} · {FAKE_BROKER}
        </div>
        <div style={{ fontSize: 11, color: "#78350F" }}>
          Wheelchair D2D · pickup 10:20 · dropoff {FAKE_FACILITY}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, idx) => (
          <motion.div
            key={r.name}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.09, duration: 0.4 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              borderRadius: 10,
              background: r.pick ? `${accent}18` : "#F8FAFC",
              border: `1px solid ${r.pick ? accent : "#E2E8F0"}`,
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0B1220" }}>{r.name}</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{r.distance} · credential score {r.score}</div>
            </div>
            {r.pick ? (
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: accent,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                }}
              >
                Assign ✓
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700 }}>Standby</div>
            )}
          </motion.div>
        ))}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: "#475569" }}>
        Human confirms in seconds. Every tap is logged to the chain-of-custody.
      </div>
    </motion.div>
  );
}

// -- Journey 05 · Facility — compliance packet
function FacilityMockup({ accent }) {
  const items = [
    { label: "Rule package", val: "NEMT · every ceremony fired" },
    { label: "Driver identity", val: `${FAKE_DRIVER} · MedexOVA Auth 96` },
    { label: "License · Insurance", val: "Current · exp 2027-08" },
    { label: "Pickup ceremony", val: "Signed at 10:04" },
    { label: "Drop-off ceremony", val: `Signed at ${FAKE_FACILITY}` },
    { label: "Broker POD-back", val: `${FAKE_BROKER_SHORT} · confirmed` },
  ];
  return (
    <motion.div style={cardBase}>
      <KickerRow accent={accent}>Compliance packet · audit-ready</KickerRow>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Hospital size={16} color={accent} weight="fill" />
        <div style={{ fontSize: 15, fontWeight: 800 }}>Trip {FAKE_TRIP_ID} · verified manifest</div>
      </div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 12, fontFamily: "ui-monospace, Menlo, monospace" }}>
        hash · {FAKE_HASH}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, idx) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.07, duration: 0.4 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 12px",
              borderRadius: 8,
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
            }}
          >
            <div style={{ fontSize: 12, color: "#065F46", fontWeight: 700 }}>{it.label}</div>
            <div
              style={{
                fontSize: 12,
                color: "#047857",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <CheckCircle size={12} weight="fill" /> {it.val}
            </div>
          </motion.div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: "#475569", fontStyle: "italic" }}>
        Billing, audit, legal — all read from the same signed source of truth.
      </div>
    </motion.div>
  );
}
